import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  CheckIcon,
  CurrencyDollarIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { ordersAPI, salesAPI } from '../../services/api';
import { Order, OrderStatus, OrderType, OrderPaymentStatus, PayOrderDto } from '../../types';
import { useUser } from '../UserContext';
import { toastSuccess, toastError } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

const OrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const { t } = useTranslation();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [makingPartialPayment, setMakingPartialPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState<PayOrderDto>({
    items: [],
    employeeNotes: '',
  });
  const [payAmount, setPayAmount] = useState<number>(0);
  const [isFullPay, setIsFullPay] = useState<boolean>(true);
  const [paymentHistory, setPaymentHistory] = useState<any>(null);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const canDelete = (currentUser?.role === 'owner' || currentUser?.role === 'admin');

  useEffect(() => {
    if (id) {
      loadOrder();
    }
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersAPI.getById(id!);
      setOrder(data);
      
      // Initialize payment data with order items
      if (data.items) {
        setPaymentData({
          items: data.items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discount: item.discount,
            notes: item.notes,
          })),
          employeeNotes: '',
          actualSubtotal: data.subtotal,
          actualTax: data.tax,
          actualDiscount: data.discount,
        });
        setPayAmount(data.remainingAmount || 0);
        setIsFullPay(true);
      }
    } catch (err) {
      setError(t('orders.detail.errorLoad'));
      console.error('Error loading order:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayOrder = async () => {
    if (!order) return;
    
    try {
      setPaying(true);
      setError(null);
      const remaining = Number(order.remainingAmount) || 0;
      const amountToPay = Number(payAmount) || 0;

      if (!isFullPay && amountToPay > 0 && amountToPay < remaining) {
        // Partial payment flow
        await ordersAPI.makePartialPayment(order.id, {
          amount: amountToPay,
          notes: paymentData.employeeNotes,
        });
      } else {
        // Full payment flow
        const normalized = {
          ...paymentData,
          actualSubtotal: paymentData.actualSubtotal === undefined ? Number(order.subtotal) : Number(paymentData.actualSubtotal) || 0,
          actualTax: paymentData.actualTax === undefined ? Number(order.tax) : Number(paymentData.actualTax) || 0,
          actualDiscount: paymentData.actualDiscount === undefined ? Number(order.discount) : Number(paymentData.actualDiscount) || 0,
          items: (paymentData.items || []).map(it => ({
            ...it,
            quantity: Number(it.quantity) || 0,
            unitPrice: Number(it.unitPrice) || 0,
            discount: it.discount === undefined ? 0 : Number(it.discount) || 0,
          })),
        };
        await ordersAPI.payOrder(order.id, normalized);
      }
      await loadOrder(); // Reload to get updated status
      setShowPaymentModal(false);
    } catch (err: any) {
      console.error('Error paying order:', err);
      setError(t('orders.detail.errors.pay'));
    } finally {
      setPaying(false);
    }
  };

  const handleViewPaymentHistory = async () => {
    if (!order) return;
    
    try {
      setError(null);
      const history = await ordersAPI.getPaymentHistory(order.id);
      setPaymentHistory(history);
      setShowPaymentHistory(true);
    } catch (err: any) {
      console.error('Error loading payment history:', err);
      setError(t('orders.detail.errors.loadPaymentHistory'));
    }
  };


  const getStatusBadge = (status: OrderStatus) => {
    const statusConfig = {
      [OrderStatus.ASSIGNED]: 'bg-blue-100 text-blue-800 border-blue-200',
      [OrderStatus.IN_PROGRESS]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      [OrderStatus.COMPLETED]: 'bg-green-100 text-green-800 border-green-200',
      [OrderStatus.CANCELLED]: 'bg-red-100 text-red-800 border-red-200',
      [OrderStatus.EXPIRED]: 'bg-gray-100 text-gray-800 border-gray-200',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusConfig[status]}`}>
        <div className={`w-2 h-2 rounded-full mr-2 ${
          status === OrderStatus.ASSIGNED ? 'bg-blue-500' :
          status === OrderStatus.IN_PROGRESS ? 'bg-yellow-500' :
          status === OrderStatus.COMPLETED ? 'bg-green-500' :
          status === OrderStatus.CANCELLED ? 'bg-red-500' : 'bg-gray-500'
        }`}></div>
        {t(`orders.statuses.${status}`)}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus: OrderPaymentStatus) => {
    const paymentStatusConfig = {
      [OrderPaymentStatus.UNPAID]: 'bg-red-100 text-red-800 border-red-200',
      [OrderPaymentStatus.PARTIALLY_PAID]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      [OrderPaymentStatus.PAID]: 'bg-green-100 text-green-800 border-green-200',
      [OrderPaymentStatus.OVERPAID]: 'bg-purple-100 text-purple-800 border-purple-200',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${paymentStatusConfig[paymentStatus]}`}>
        <div className={`w-2 h-2 rounded-full mr-2 ${
          paymentStatus === OrderPaymentStatus.UNPAID ? 'bg-red-500' :
          paymentStatus === OrderPaymentStatus.PARTIALLY_PAID ? 'bg-yellow-500' :
          paymentStatus === OrderPaymentStatus.PAID ? 'bg-green-500' :
          paymentStatus === OrderPaymentStatus.OVERPAID ? 'bg-purple-500' : 'bg-gray-500'
        }`}></div>
        {t(`orders.paymentStatuses.${paymentStatus}`)}
      </span>
    );
  };

  const getTypeBadge = (type: OrderType) => {
    const typeConfig = {
      [OrderType.ONE_TIME]: 'bg-purple-100 text-purple-800 border-purple-200',
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${typeConfig[type]}`}>
        {t(`orders.types.${type}`)}
      </span>
    );
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const canPayOrder = order && 
    (order.paymentStatus === OrderPaymentStatus.UNPAID || order.paymentStatus === OrderPaymentStatus.PARTIALLY_PAID) &&
    (currentUser?.id === order.assignedTo || currentUser?.role === 'admin' || currentUser?.role === 'owner');

  const canMakePartialPayment = order && 
    order.paymentStatus === OrderPaymentStatus.UNPAID &&
    (currentUser?.id === order.assignedTo || currentUser?.role === 'admin' || currentUser?.role === 'owner');

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-xl p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-danger-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-danger-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-danger-800">{t('orders.detail.errorTitle')}</h3>
            <p className="text-sm text-danger-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Link
              to="/orders"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('orders.detail.headerTitle', { id: order.id.slice(0, 8) })}</h1>
              <p className="mt-1 text-sm text-gray-600">{t('orders.detail.headerSubtitle')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {canPayOrder && (
              <button
                onClick={() => setShowPaymentModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50"
              >
                <CreditCardIcon className="h-4 w-4 mr-2" />
                {t('orders.detail.actions.recordPayment')}
              </button>
            )}
            {order && order.status === OrderStatus.ASSIGNED && (
              <button
                onClick={async () => {
                  try {
                    setError(null);
                    await ordersAPI.update(order.id, { status: OrderStatus.IN_PROGRESS });
                    toastSuccess(t('orders.detail.toast.inProgress'));
                    await loadOrder();
                  } catch (e) {
                    setError(t('orders.detail.errors.updateInProgress'));
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all duration-200"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                {t('orders.detail.actions.markInProgress')}
              </button>
            )}
            {order && order.status === OrderStatus.IN_PROGRESS && (
              <button
                onClick={async () => {
                  try {
                    setError(null);
                    await ordersAPI.completeOrder(order.id, { employeeNotes: paymentData.employeeNotes, actualSubtotal: order.subtotal, actualTax: order.tax, actualDiscount: order.discount });
                    toastSuccess(t('orders.detail.toast.completed'));
                    await loadOrder();
                  } catch (e) {
                    setError(t('orders.detail.errors.complete'));
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                {t('orders.detail.actions.markCompleted')}
              </button>
            )}
            <Link
              to={`/orders/${order.id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {t('orders.detail.actions.edit')}
            </Link>
            {canDelete && (
              <button
                onClick={async () => {
                  if (!order) return;
                  if (!window.confirm(t('orders.detail.confirmDelete'))) return;
                  try {
                    await ordersAPI.delete(order.id);
                    toastSuccess(t('orders.detail.toast.deleted'));
                    navigate('/orders');
                  } catch (e) {
                    toastError(t('orders.detail.toast.deleteFailed'));
                  }
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                {t('orders.detail.actions.delete')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Order Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.info.title')}</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900">{t('orders.detail.orderLabel', { id: order.id.slice(0, 8) })}</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {getStatusBadge(order.status)}
                    {getTypeBadge(order.type)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('orders.detail.info.assignedDate')}</p>
                    <p className="text-sm text-gray-600">{formatDate(order.assignedDate)}</p>
                  </div>
                </div>

                {order.dueDate && (
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('orders.detail.info.dueDate')}</p>
                      <p className="text-sm text-gray-600">{formatDate(order.dueDate)}</p>
                    </div>
                  </div>
                )}

                {order.completedDate && (
                  <div className="flex items-center space-x-3">
                    <CheckIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('orders.detail.info.completedDate')}</p>
                      <p className="text-sm text-gray-600">{formatDate(order.completedDate)}</p>
                    </div>
                  </div>
                )}



                <div className="flex items-center space-x-3">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('orders.detail.info.assignedTo')}</p>
                    <p className="text-sm text-gray-600">
                      {order.assignedToUser ? 
                        `${order.assignedToUser.firstName} ${order.assignedToUser.lastName}` : 
                        t('common.na')
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          {order.customerName && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.customer.title')}</h2>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                    <BuildingOfficeIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900">{order.customerName}</h3>
                  <div className="mt-2 space-y-1">
                    {order.customerEmail && (
                      <p className="text-sm text-gray-600">{order.customerEmail}</p>
                    )}
                    {order.customerPhone && (
                      <p className="text-sm text-gray-600">{order.customerPhone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Order Items */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.items.title')}</h2>
            <div className="space-y-4">
              {order.items?.map((item, index) => (
                <div key={item.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {item.product?.name || t('orders.detail.items.productFallback', { index: index + 1 })}
                        </p>
                        <p className="text-sm text-gray-600">
                          {t('orders.detail.items.qty')}: {item.quantity} × {formatCurrency(item.unitPrice)}
                          {item.discount > 0 && ` - ${formatCurrency(item.discount)} ${t('orders.detail.items.discount')}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {formatCurrency(item.total)}
                        </p>
                      </div>
                    </div>
                    {item.notes && (
                      <p className="text-sm text-gray-500 mt-1">{item.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Financial Summary */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.financial.title')}</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('orders.detail.financial.subtotal')}</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(order.subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('orders.detail.financial.tax')}</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(order.tax)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('orders.detail.financial.discount')}</span>
                  <span className="text-sm font-medium text-gray-900">-{formatCurrency(order.discount)}</span>
                </div>
              )}
              <div className="border-t pt-3">
                <div className="flex justify-between">
                  <span className="text-base font-medium text-gray-900">{t('orders.detail.financial.total')}</span>
                  <span className="text-base font-bold text-gray-900">{formatCurrency(order.totalAmount)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.payment.title')}</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('orders.detail.payment.status')}</span>
                <div>{getPaymentStatusBadge(order.paymentStatus)}</div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('orders.detail.payment.paidAmount')}</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(order.paidAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">{t('orders.detail.payment.remainingAmount')}</span>
                <span className="text-sm font-medium text-gray-900">{formatCurrency(order.remainingAmount)}</span>
              </div>
              {order.firstPaymentDate && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('orders.detail.payment.firstPayment')}</span>
                  <span className="text-sm text-gray-900">{formatDate(order.firstPaymentDate)}</span>
                </div>
              )}
              {order.lastPaymentDate && (
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('orders.detail.payment.lastPayment')}</span>
                  <span className="text-sm text-gray-900">{formatDate(order.lastPaymentDate)}</span>
                </div>
              )}
              {order.paidAmount > 0 && (
                <div className="pt-3 border-t">
                  <button
                    onClick={handleViewPaymentHistory}
                    className="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                  >
                    <CurrencyDollarIcon className="h-4 w-4 mr-2" />
                    {t('orders.detail.payment.viewHistory')}
                  </button>
                </div>
              )}
            </div>
          </div>

          

          {/* Notes */}
          {order.notes && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.notes.title')}</h2>
              <p className="text-sm text-gray-600">{order.notes}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('orders.detail.quick.title')}</h2>
            <div className="space-y-3">
              <Link
                to={`/orders/${order.id}/edit`}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {t('orders.detail.quick.edit')}
              </Link>
              <Link
                to="/orders"
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('orders.detail.quick.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Unified Payment Modal (Full or Partial) */}
      {showPaymentModal && order && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative p-8 border w-full max-w-md max-h-full">
            <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
              <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('orders.detail.payModal.title', { id: order.id.slice(0, 8) })}
                </h3>
                <button
                  type="button"
                  className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                  onClick={() => setShowPaymentModal(false)}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="space-y-3">
                  <label className="flex items-center space-x-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!isFullPay}
                      onChange={(e) => setIsFullPay(!e.target.checked ? true : false)}
                    />
                    <span>{t('orders.detail.payModal.partialLabel')}</span>
                  </label>
                  {!isFullPay && (
                    <div>
                      <label htmlFor="payAmount" className="block text-sm font-medium text-gray-900 dark:text-gray-300">
                        {t('orders.detail.payModal.amountLabel', { remaining: formatCurrency(order.remainingAmount) })}
                      </label>
                      <input
                        type="number"
                        id="payAmount"
                        min={0}
                        max={Number(order.remainingAmount) || undefined}
                        className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400"
                        value={payAmount}
                        onChange={(e) => setPayAmount(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="employeeNotes" className="block text-sm font-medium text-gray-900 dark:text-gray-300">
                      {t('orders.form.employeeNotes')}
                    </label>
                    <textarea
                      id="employeeNotes"
                      rows={3}
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400"
                      placeholder={t('orders.form.employeeNotesPlaceholder')}
                      value={paymentData.employeeNotes}
                      onChange={(e) => setPaymentData({ ...paymentData, employeeNotes: e.target.value })}
                    />
                  </div>
                  {isFullPay && (
                  <div>
                    <label htmlFor="actualSubtotal" className="block text-sm font-medium text-gray-900 dark:text-gray-300">
                      {t('orders.form.actualSubtotal')}
                    </label>
                    <input
                      type="number"
                      id="actualSubtotal"
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400"
                      value={paymentData.actualSubtotal}
                      onChange={(e) => setPaymentData({ ...paymentData, actualSubtotal: parseFloat(e.target.value) })}
                    />
                  </div>
                  )}
                  {isFullPay && (
                  <div>
                    <label htmlFor="actualTax" className="block text-sm font-medium text-gray-900 dark:text-gray-300">
                      {t('orders.form.actualTax')}
                    </label>
                    <input
                      type="number"
                      id="actualTax"
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400"
                      value={paymentData.actualTax}
                      onChange={(e) => setPaymentData({ ...paymentData, actualTax: parseFloat(e.target.value) })}
                    />
                  </div>
                  )}
                  {isFullPay && (
                  <div>
                    <label htmlFor="actualDiscount" className="block text-sm font-medium text-gray-900 dark:text-gray-300">
                      {t('orders.form.actualDiscount')}
                    </label>
                    <input
                      type="number"
                      id="actualDiscount"
                      className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400"
                      value={paymentData.actualDiscount}
                      onChange={(e) => setPaymentData({ ...paymentData, actualDiscount: parseFloat(e.target.value) })}
                    />
                  </div>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowPaymentModal(false)}
                    className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handlePayOrder}
                    disabled={paying}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 disabled:opacity-50"
                  >
                    <CheckIcon className="h-4 w-4 mr-2" />
                    {paying ? t('orders.detail.payModal.processing') : isFullPay ? t('orders.detail.payModal.payFull') : t('orders.detail.payModal.payPartial')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      

       {/* Payment History Modal */}
       {showPaymentHistory && paymentHistory && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
           <div className="relative p-8 w-full max-w-4xl max-h-full">
             <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
               <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
                 <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                   {t('orders.detail.paymentHistory.title', { id: order?.id.slice(0, 8) })}
                 </h3>
                 <button
                   type="button"
                   className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white"
                   onClick={() => setShowPaymentHistory(false)}
                 >
                   <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                     <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                   </svg>
                 </button>
               </div>
               <div className="p-6">
                 <div className="mb-6">
                   <h4 className="text-lg font-medium text-gray-900 mb-4">{t('orders.detail.paymentHistory.summary')}</h4>
                   <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                     <div className="bg-gray-50 p-4 rounded-lg">
                       <p className="text-sm text-gray-600">{t('orders.detail.paymentHistory.totalAmount')}</p>
                       <p className="text-lg font-semibold text-gray-900">{formatCurrency(paymentHistory.totalAmount)}</p>
                     </div>
                     <div className="bg-gray-50 p-4 rounded-lg">
                       <p className="text-sm text-gray-600">{t('orders.detail.paymentHistory.paidAmount')}</p>
                       <p className="text-lg font-semibold text-green-600">{formatCurrency(paymentHistory.paidAmount)}</p>
                     </div>
                     <div className="bg-gray-50 p-4 rounded-lg">
                       <p className="text-sm text-gray-600">{t('orders.detail.paymentHistory.remainingAmount')}</p>
                       <p className="text-lg font-semibold text-red-600">{formatCurrency(paymentHistory.remainingAmount)}</p>
                     </div>
                   </div>
                 </div>
                 
                 {paymentHistory.sales && paymentHistory.sales.length > 0 ? (
                   <div>
                     <h4 className="text-lg font-medium text-gray-900 mb-4">{t('orders.detail.paymentHistory.transactions')}</h4>
                     <div className="space-y-3">
                       {paymentHistory.sales.map((sale: any, index: number) => (
                         <div key={sale.saleId} className="border border-gray-200 rounded-lg p-4">
                           <div className="flex justify-between items-start">
                             <div>
                               <p className="text-sm font-medium text-gray-900">
                                 {t('orders.detail.paymentHistory.paymentItem', { index: index + 1, invoiceNumber: sale.invoiceNumber })}
                               </p>
                               <p className="text-sm text-gray-600">
                                 {formatDate(sale.completedAt)}
                               </p>
                               <p className="text-sm text-gray-600">
                                 {t('orders.detail.paymentHistory.method', { method: sale.paymentMethod.replace('_', ' ') })}
                               </p>
                             </div>
                             <div className="text-right">
                               <p className="text-lg font-semibold text-green-600">
                                 {formatCurrency(sale.amount)}
                               </p>
                             </div>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>
                 ) : (
                   <div className="text-center py-8">
                     <p className="text-gray-500">{t('orders.detail.paymentHistory.none')}</p>
                   </div>
                 )}
                 
                 <div className="flex justify-end mt-6">
                   <button
                     onClick={() => setShowPaymentHistory(false)}
                     className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                   >
                     {t('orders.detail.paymentHistory.close')}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default OrderDetail;
